/*
 * Copyright 2025 GnosisPay
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { type CSSProperties } from "react";

export type Environment = "production" | "staging" | "development";

export enum Action {
  SetPin = "SetPin",
  DoneSettingPin = "DoneSettingPin",
  CardExpirationCopied = "CardExpirationCopied",
  CardSecurityCodeCopied = "CardSecurityCodeCopied",
  CardNumberCopied = "CardNumberCopied",
}

export type ActionSuccessCallback = (action: Action) => void;

interface GPSDKInitConfigBase {
  appId: string;
  ephemeralToken: string;
  environment?: Environment;
  iframeHost?: string;
  onActionSuccess?: ActionSuccessCallback;
  onInvalidToken?: EphemeralTokenInvalidCallback;
  onError?: ErrorCallback;
}

/** V1 config (default) — GnosisPay API auth token + card token passed via init(). */
export interface GPSDKInitConfigV1 extends GPSDKInitConfigBase {
  pseVersion?: 1;
  gnosisPayApiAuthToken: string;
  authModuleToken?: never;
}

/** V3 config — auth module token + EIP-712 signature for 2FA. */
export interface GPSDKInitConfigV3 extends GPSDKInitConfigBase {
  pseVersion: 3;
  authModuleToken: string;
  /** EIP-712 typed-data signature produced by the user's wallet for this specific action. */
  eip712Signature: string;
  /** Nonce value returned by the challenge endpoint (decimal string, uint256). */
  eip712Nonce: string;
  gnosisPayApiAuthToken?: never;
}

export type GPSDKInitConfig = GPSDKInitConfigV1 | GPSDKInitConfigV3;

export interface GPSDKConfig extends GPSDKInitConfigBase {
  pseVersion: 1 | 3;
  environment: Environment;
  iframeHost: string;
  // v1
  gnosisPayApiAuthToken?: string;
  // v3
  authModuleToken?: string;
  eip712Signature?: string;
  eip712Nonce?: string;
}

export enum ElementType {
  CardData = "cardData",
  CardPin = "cardPin",
  SetCardPin = "setCardPin",
  CardExpirationCopied = "cardExpirationCopied",
  CardSecurityCodeCopied = "cardSecurityCodeCopied",
  CardNumberCopied = "cardNumberCopied",
}

export type ElementStyle = {
  container?: CSSProperties;
  field?: CSSProperties;
  label?: CSSProperties;
  value?: CSSProperties;
  changePinButton?: CSSProperties;
  copyButton?: CSSProperties;
  successNotice?: CSSProperties;
};

/** V1 element options — requires cardToken. */
export interface ElementOptionsV1 {
  cardToken: string;
  style?: ElementStyle;
}

/** V2 element options — requires cardId. */
export interface ElementOptionsV2 {
  cardId: string;
  style?: ElementStyle;
}

export type ElementOptions = ElementOptionsV1 | ElementOptionsV2;

export interface SDKMessage {
  type: "init" | "tokenRefresh";
  ephemeralToken: string;
  elementType?: ElementType;
  appId?: string;
  version?: string;
  style?: ElementStyle;
  // v1
  cardToken?: string;
  gnosisPayApiAuthToken?: string;
  // v2+
  authModuleToken?: string;
  cardId?: string;
  // v3
  eip712Signature?: string;
  eip712Nonce?: string;
}

// Message types from iframe to SDK
export enum IframeMessageType {
  SetPinSuccess = "SetPinSuccess",
  CardExpirationCopied = "CardExpirationCopied",
  CardSecurityCodeCopied = "CardSecurityCodeCopied",
  CardNumberCopied = "CardNumberCopied",
  DoneSettingPin = "DoneSettingPin",
  EphemeralTokenInvalid = "EphemeralTokenInvalid",
  Error = "Error",
}

export interface IframeMessage {
  type: IframeMessageType;
  message?: string;
  details?: Record<string, any>;
}

// Callback types for specific message handling
export type SetPinSuccessCallback = () => void;
export type EphemeralTokenInvalidCallback = (message?: string) => void;
export type ErrorCallback = (
  message?: string,
  details?: Record<string, any>
) => void;
